import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { useCreation } from 'ahooks';
import { Alert, AlertProps, Checkbox, Form, Radio, Tooltip } from 'antd';
import RoleCascader, { useRoleCascader } from 'bizComponents/RoleCascader';
import { getStoreCount, StoreScopesModal, StoreScopesModalProps } from 'components/StoreScopes';
import { GreyRadioGroup, RadioCard } from 'components/styled/radio';
import useModal from 'hooks/useModal';
import { cloneDeep, isNil, noop, uniqBy } from 'lodash-es';
import { ReactNode, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { RoleWithDepartment } from 'services/api/department/roleWithDepartment';
import type { AccountInDepartment } from '../../DepartmentAccount/DepartmentAccountDetail/DepartmentAccountDetailForm';
import ScopePreviewModal from '../ScopePreviewModal';
import ScopeTag from '../ScopeTag';
import { What } from 'views/Marketing/components/PolicyTable';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { QuestionCircleOutlined } from '@ant-design/icons';

const FormItem = Form.Item;
const CheckboxGroup = styled(Checkbox.Group)`
  > label.ant-checkbox-wrapper {
    margin-left: 0;
    margin-right: 8px;
    margin-bottom: 4px;
    &:last-of-type {
      margin-right: 0;
    }
  }
`;
const ScopeWrapper = styled.div`
  margin-top: -15px;
  margin-bottom: 20px;
  padding: 12px;
  border: solid 1px #f0f2f5;
`;

const StyledAlert = styled(Alert)`
  align-items: flex-start;
  font-size: 13px;
  > span {
    margin-top: 3px;
  }
  .ant-alert-message {
    > div > span {
      font-weight: bold;
    }
  }
`;

export function ModeAlert(props: AlertProps) {
  const { t } = useTranslate();

  return (
    <StyledAlert
      banner
      type="info"
      message={[
        <div>
          <span>{t('department.智能分配')}：</span>
          {t(
            'department.接待范围内的会话将根据客服接待效率优先的原则进行分配，每个帐号只能看到分配给自己的会话，通常适用于客服人员。',
          )}
        </div>,
        <div>
          <span>{t('department.监督全部')}：</span>
          {t(
            'department.监督店铺范围内的全部会话均可见且可回复，但回复后不会改变会话对应的主接待客服帐号，通常适合于管理人员。',
          )}
        </div>,
        <div>
          <span>{t('department.不予分配')}：</span>
          {t(
            'department.买家发起咨询后不会分配给该客服，但客服仍可以向近期有订单的买家发起主动沟通，或接收其他客服转接的会话，通常适合于运营人员或专属的售后客服。',
          )}
        </div>,
      ]}
      {...props}
    />
  );
}

export type RoleIdAccountsMap = Partial<Record<number, AccountInDepartment[]>>;

export interface ReceptionConfigProps {
  disabled?: boolean;
  departmentId: StoreHomeV2.Department['id'];
  // 当前操作的角色id（如有），用于指定替补角色时过滤自身(自己不能为自己替补)
  roleId: StoreHomeV2.RoleWithDepartment['id'] | undefined;
  name: string;
  /**
   * 管理员视角权限，不传视为无管理员视角权限
   */
  superviserPermission?: RoleWithDepartment['permissionWithScopes'][number];
  /**
   * 修改管理员视角权限，参数 permission 不传视为删除管理员视角权限
   */
  onSuperviserPermissionChange?: (
    permission?: RoleWithDepartment['permissionWithScopes'][number],
  ) => void;
  showScopes?: boolean;
  departmentScopes?: General.StoreScope[];
  roleIdAccountsMap?: RoleIdAccountsMap;
}

const StoreScopesModalItem = ({
  children,
  onChange,
  disabled = false,
  value,
  visible,
  onCancel,
  ...otherProps
}: StoreScopesModalProps & {
  children: (props: Pick<StoreScopesModalProps, 'onChange'>) => ReactNode;
  disabled?: boolean;
}) => {
  const modal = disabled ? (
    <ScopePreviewModal value={value || []} visible={visible} onCancel={onCancel} />
  ) : (
    <StoreScopesModal
      showFooterTips={false}
      value={value}
      onChange={onChange}
      visible={visible}
      onCancel={onCancel}
      {...otherProps}
    />
  );

  return (
    <>
      {modal}
      {children({ onChange })}
    </>
  );
};

const genChildNamePath = (prefix: string, child: string) => [prefix, child];
/**
 * @param props
 * @returns
 */
const AssignConversationConfigFields = (props: ReceptionConfigProps) => {
  const {
    name = 'assignConversationConfig',
    departmentId,
    disabled,
    roleId,
    superviserPermission,
    onSuperviserPermissionChange = noop,
    showScopes = true,
    departmentScopes = [],
    roleIdAccountsMap,
    ...otherProps
  } = props;

  const { t } = useTranslate();
  const storeScopeModal = useModal();

  const { whiteListConfig: { ASSIGN_REVIEW_AND_CANCEL_TRADE } = {} } = useOrgConfigStateValue();

  const substituteGroupLabelMap = {
    NO_SUBSTITUTE: t('department_role_no_substitute'),
    SPECIFIC_ROLE: t('department.指定角色参与替补接待'),
    ALL_ROLE: t('department.部门下所有接待角色均参与替补接待'),
  };
  const {
    request: { data: roleCascaderDataSource },
  } = useRoleCascader();
  const receptionRoleCascaderDataSource = useCreation(() => {
    if (!roleCascaderDataSource?.length) {
      return [];
    }
    return roleCascaderDataSource.reduce((r, dept) => {
      if (dept.id !== departmentId) {
        return r;
      }
      const validRoles = dept?.roles?.filter(
        (r) => r.assignConversation === true && !(!isNil(roleId) && r.id === roleId),
      );
      if (!validRoles?.length) {
        return r;
      }
      r.push(cloneDeep({ ...dept, roles: validRoles }));
      return r;
    }, [] as StoreHomeV2.DepartmentWithRoles[]);
  }, [roleCascaderDataSource]);

  const nonReceptionAccounts =
    roleIdAccountsMap && roleId
      ? roleIdAccountsMap[roleId]?.filter(({ userType }) => userType === 2) || []
      : [];

  const hasShopee = useMemo(
    () => getStoreCount(departmentScopes.filter(({ platform }) => platform === 'SHOPEE')) > 0,
    [departmentScopes],
  );

  const fields = {
    assignConversation: genChildNamePath(name, 'assignConversation'),
    substituteType: genChildNamePath(name, 'substituteType'),
    receptionScope: genChildNamePath(name, 'receptionScope'),
    tradeStatusFilters: genChildNamePath(name, 'tradeStatusFilters'),
    saleBuyerStatus: genChildNamePath(name, 'saleBuyerStatus'),
    buyerFilters: genChildNamePath(name, 'buyerFilters'),
    assignBuyerAndOrderRule: genChildNamePath(name, 'assignBuyerAndOrderRule'),
    substituteRoleId: genChildNamePath(name, 'substituteRoleId'),
    platformStoreScopes: genChildNamePath(name, 'platformStoreScopes'),
    receptionConfigs: genChildNamePath(name, 'receptionConfigs'),
  };
  return (
    <FormItem noStyle shouldUpdate {...otherProps}>
      {({ getFieldValue }) => {
        const isAssignConversation = getFieldValue(fields.assignConversation);
        return (
          <>
            <FormItem
              label={t('department.买家发起咨询后接待量分配模式')}
              required
              dependencies={[fields.assignConversation]}
            >
              {() => {
                return (
                  <>
                    <FormItem
                      noStyle
                      getValueProps={(v) => {
                        if (superviserPermission) {
                          return { value: -1 };
                        }

                        return { value: v ? 1 : 0 };
                      }}
                      normalize={(value, preValue) => {
                        if (value === -1) {
                          if (!superviserPermission) {
                            onSuperviserPermissionChange({
                              permissionId: 2,
                              permissionName: '管理员视角',
                              permissionGroupName: '消息',
                              platformStoreScopes: [],
                            });
                          }

                          return preValue;
                        }

                        if (superviserPermission) {
                          onSuperviserPermissionChange(undefined);
                        }

                        return value === 1;
                      }}
                      name={fields.assignConversation}
                    >
                      <Radio.Group disabled={disabled}>
                        {nonReceptionAccounts.length > 0 ? (
                          <Tooltip
                            title={t('account.该角色下有x接待限额帐号，不支持选择智能分配模式', {
                              x: nonReceptionAccounts.length,
                            })}
                          >
                            <RadioCard value={1} disabled>
                              {t('department.智能分配')}
                            </RadioCard>
                          </Tooltip>
                        ) : (
                          <RadioCard value={1}>{t('department.智能分配')}</RadioCard>
                        )}
                        <RadioCard value={-1}>{t('department.监督全部')}</RadioCard>
                        <RadioCard value={0}>{t('department.不予分配')}</RadioCard>
                      </Radio.Group>
                    </FormItem>
                    <ModeAlert style={{ marginTop: '6px' }} />
                  </>
                );
              }}
            </FormItem>
            {superviserPermission !== undefined && showScopes && (
              <FormItem required label={t('department.监督店铺范围')}>
                <StoreScopesModalItem
                  value={superviserPermission.platformStoreScopes}
                  onChange={(platformStoreScopes) => {
                    onSuperviserPermissionChange({
                      ...superviserPermission,
                      platformStoreScopes,
                    });
                  }}
                  disabled={disabled}
                  departmentId={departmentId}
                  visible={storeScopeModal.visible}
                  destroyOnClose
                  onCancel={() => storeScopeModal.hide()}
                >
                  {({ onChange = noop }) => (
                    <ScopeTag
                      value={superviserPermission.platformStoreScopes}
                      departmentScopes={departmentScopes}
                      disabled={disabled}
                      onClick={() => storeScopeModal.show()}
                      onClear={() => onChange([])}
                    />
                  )}
                </StoreScopesModalItem>
              </FormItem>
            )}
            {isAssignConversation && !superviserPermission ? (
              <>
                <FormItem noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const platformStoreScopes = getFieldValue(fields.platformStoreScopes) || [];

                    return (
                      showScopes && (
                        <>
                          <FormItem
                            label={t('department.接待店铺范围')}
                            name={fields.platformStoreScopes}
                            required
                            dependencies={[fields.assignConversation]}
                            rules={[
                              {
                                required: true,
                                validator: async (_, value) => {
                                  if (!Array.isArray(value) || value?.length < 1) {
                                    throw new Error(
                                      t('department.请选择x', {
                                        x: t('department.接待店铺范围'),
                                      }),
                                    );
                                  }
                                  const count = getStoreCount(value);
                                  if (!count) {
                                    throw new Error(
                                      t('department.请选择x', {
                                        x: t('department.接待店铺范围'),
                                      }),
                                    );
                                  }
                                },
                              },
                            ]}
                          >
                            <StoreScopesModalItem
                              disabled={disabled}
                              departmentId={departmentId}
                              visible={storeScopeModal.visible}
                              destroyOnClose
                              onCancel={() => storeScopeModal.hide()}
                            >
                              {({ onChange = noop }) => (
                                <ScopeTag
                                  value={platformStoreScopes}
                                  departmentScopes={departmentScopes}
                                  disabled={disabled}
                                  onClick={() => storeScopeModal.show()}
                                  onClear={() => onChange(departmentScopes)}
                                />
                              )}
                            </StoreScopesModalItem>
                          </FormItem>
                        </>
                      )
                    );
                  }}
                </FormItem>
                <FormItem
                  noStyle
                  dependencies={[
                    fields.substituteType,
                    fields.receptionScope,
                    fields.tradeStatusFilters,
                    fields.buyerFilters,
                  ]}
                >
                  {(form) => {
                    const { getFieldValue } = form;
                    const substituteType = getFieldValue(fields.substituteType);
                    const receptionScope = getFieldValue(fields.receptionScope);
                    const tradeStatusFilters = getFieldValue(fields.tradeStatusFilters);
                    const buyerFilters = getFieldValue(fields.buyerFilters);
                    const receptionConfigs = getFieldValue(fields.receptionConfigs);
                    return (
                      <>
                        <FormItem
                          name={fields.receptionScope}
                          required
                          label={t('department.接待买家范围')}
                        >
                          <GreyRadioGroup disabled={disabled}>
                            {['ALL_BUYER', 'TRADE_STATUS', 'BUYER_SALE_STATUS', 'BUYER_LABEL'].map(
                              (key) => {
                                return (
                                  <Radio.Button key={key} value={key}>
                                    {t(`receptionScopeType.${key}`)}
                                  </Radio.Button>
                                );
                              },
                            )}
                          </GreyRadioGroup>
                        </FormItem>

                        {receptionScope === 'TRADE_STATUS' && (
                          <ScopeWrapper>
                            <FormItem
                              name={fields.tradeStatusFilters}
                              rules={[
                                {
                                  required: true,
                                  async validator(_, value) {
                                    if (!value || value.length < 1) {
                                      throw new Error(t('validate.tradeStatusEmpty'));
                                    }
                                  },
                                },
                              ]}
                            >
                              <CheckboxGroup disabled={disabled} value={tradeStatusFilters}>
                                {[
                                  'NO_TRADE',
                                  'NO_PAY',
                                  'WAIT_SHIP',
                                  'SHIPPING',
                                  'RECENT_TRADED',
                                  'REFUND',
                                ].map((key) => {
                                  return (
                                    <Checkbox key={key} value={key}>
                                      {t(`tradeStatus.${key}`)}
                                    </Checkbox>
                                  );
                                })}
                              </CheckboxGroup>
                            </FormItem>
                          </ScopeWrapper>
                        )}
                        {receptionScope === 'BUYER_SALE_STATUS' && (
                          <ScopeWrapper>
                            <FormItem
                              name={fields.saleBuyerStatus}
                              getValueProps={(saleBuyerStatus) => {
                                return {
                                  value: Object.keys(saleBuyerStatus).reduce<any>((res, key) => {
                                    if (saleBuyerStatus[key]) {
                                      res.push(key);
                                    }
                                    return res;
                                  }, []),
                                };
                              }}
                              normalize={(list) =>
                                list.reduce(
                                  (res: any, key: any) => {
                                    res[key] = true;
                                    return res;
                                  },
                                  {
                                    isPreSaleBuyer: false,
                                    isSaleBuyer: false,
                                    isAfterSaleBuyer: false,
                                  },
                                )
                              }
                              rules={[
                                {
                                  required: true,
                                  async validator(_, value) {
                                    if (!value || Object.values(value).every((_) => !_)) {
                                      throw new Error(t('refundSetting.请选择'));
                                    }
                                  },
                                },
                              ]}
                            >
                              <CheckboxGroup disabled={disabled}>
                                {['isPreSaleBuyer', 'isSaleBuyer', 'isAfterSaleBuyer'].map(
                                  (key) => {
                                    return (
                                      <Checkbox key={key} value={key}>
                                        {
                                          {
                                            isPreSaleBuyer: t('aide.售前买家'),
                                            isSaleBuyer: t('aide.售中买家'),
                                            isAfterSaleBuyer: t('aide.售后买家'),
                                          }[key]
                                        }
                                      </Checkbox>
                                    );
                                  },
                                )}
                              </CheckboxGroup>
                            </FormItem>
                          </ScopeWrapper>
                        )}
                        {receptionScope === 'BUYER_LABEL' && (
                          <ScopeWrapper>
                            <FormItem
                              name={fields.buyerFilters}
                              rules={[
                                {
                                  required: true,
                                  async validator(_, value) {
                                    if (!value || value.length < 1) {
                                      throw new Error(t('validate.buyerTagEmpty'));
                                    }
                                  },
                                },
                              ]}
                            >
                              <CheckboxGroup disabled={disabled} value={buyerFilters}>
                                {['NEW', 'OLD'].map((key) => {
                                  return (
                                    <Checkbox key={key} value={key}>
                                      {t(`buyerTag.${key}`)}
                                    </Checkbox>
                                  );
                                })}
                              </CheckboxGroup>
                            </FormItem>
                          </ScopeWrapper>
                        )}
                        {hasShopee && (
                          <FormItem
                            name={fields.receptionConfigs}
                            required
                            label={
                              <div>
                                {t('Shopee买家&达人接待配置')}
                                <Tooltip title={t('设置仅对Shopee店铺生效')}>
                                  <QuestionCircleOutlined
                                    style={{ marginLeft: 4, cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              </div>
                            }
                          >
                            <Checkbox.Group>
                              <Checkbox
                                value="SHOPEE_BUYER"
                                disabled={
                                  receptionConfigs?.includes('SHOPEE_BUYER') &&
                                  !receptionConfigs?.includes('SHOPEE_AFFILIATE')
                                }
                              >
                                {t(
                                  'scene.chatWorkspace.conversationList.quickFilter.action.shopeeBuyerChat',
                                )}
                              </Checkbox>
                              <Checkbox
                                value="SHOPEE_AFFILIATE"
                                disabled={
                                  receptionConfigs?.includes('SHOPEE_AFFILIATE') &&
                                  !receptionConfigs?.includes('SHOPEE_BUYER')
                                }
                              >
                                {t(
                                  'scene.chatWorkspace.conversationList.quickFilter.action.shopeeAffiliateChat',
                                )}
                              </Checkbox>
                            </Checkbox.Group>
                          </FormItem>
                        )}
                        <FormItem
                          name={fields.substituteType}
                          required
                          label={t('department.当前角色繁忙时替补接待方案')}
                        >
                          <GreyRadioGroup disabled={disabled}>
                            {Object.keys(substituteGroupLabelMap).map((key) => {
                              return (
                                <Radio.Button key={key} value={key}>
                                  {substituteGroupLabelMap[key]}
                                </Radio.Button>
                              );
                            })}
                          </GreyRadioGroup>
                        </FormItem>
                        {substituteType === 'SPECIFIC_ROLE' && (
                          <ScopeWrapper>
                            <FormItem
                              className={css`
                                margin-bottom: 10px;
                              `}
                              normalize={(v) => v.roleId}
                              name={fields.substituteRoleId}
                              rules={[
                                {
                                  required: true,
                                  validator: async (_, roleId) => {
                                    if (!roleId) {
                                      throw new Error(t('department.请选择替补接待的角色'));
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <RoleCascader
                                disabled={disabled}
                                dataSource={receptionRoleCascaderDataSource}
                              />
                            </FormItem>
                          </ScopeWrapper>
                        )}
                      </>
                    );
                  }}
                </FormItem>
              </>
            ) : null}
            {ASSIGN_REVIEW_AND_CANCEL_TRADE && !superviserPermission && (
              <FormItem
                shouldUpdate
                label={
                  <div>
                    {t('cancel_order_and_negative_review_reception_rule_label')}
                    <Tooltip title={t('cancel_order_and_negative_review_reception_rule_tips')}>
                      <What />
                    </Tooltip>
                  </div>
                }
              >
                {({ getFieldValue, setFields }) => {
                  const assignBuyerAndOrderRule = getFieldValue(fields.assignBuyerAndOrderRule);
                  const permissionWithScopes = getFieldValue('permissionWithScopes');
                  return (
                    <div className="px-[16px] py-[12px] bg-[#F7F9FA]">
                      <FormItem noStyle name={fields.assignBuyerAndOrderRule} />
                      <Checkbox
                        checked={assignBuyerAndOrderRule?.isAssignAfterSalesOrder}
                        onChange={({ target: { checked } }) => {
                          setFields([
                            {
                              name: [...fields.assignBuyerAndOrderRule, 'isAssignAfterSalesOrder'],
                              value: checked,
                            },
                            ...(checked
                              ? [
                                  {
                                    name: 'permissionWithScopes',
                                    value: uniqBy(
                                      [
                                        ...permissionWithScopes,
                                        {
                                          permissionId: permissionNameIdMap['弃单处理页面'],
                                          permissionName: '弃单处理页面',
                                          permissionGroupName: '消息',
                                          platformStoreScopes:
                                            getFieldValue(fields.platformStoreScopes) || [],
                                        },
                                        {
                                          permissionId: permissionNameIdMap['操作弃单'],
                                          permissionName: '操作弃单',
                                          permissionGroupName: '消息',
                                          platformStoreScopes:
                                            getFieldValue(fields.platformStoreScopes) || [],
                                        },
                                      ],
                                      'permissionId',
                                    ),
                                  },
                                ]
                              : []),
                          ]);
                        }}
                      >
                        {t('cancel_order_reception')}
                      </Checkbox>
                      <Checkbox
                        checked={assignBuyerAndOrderRule?.isAssignNegativeReview}
                        onChange={({ target: { checked } }) => {
                          setFields([
                            {
                              name: [...fields.assignBuyerAndOrderRule, 'isAssignNegativeReview'],
                              value: checked,
                            },
                            ...(checked
                              ? [
                                  {
                                    name: 'permissionWithScopes',
                                    value: uniqBy(
                                      [
                                        ...permissionWithScopes,
                                        {
                                          permissionId: permissionNameIdMap['访问评价聚合页'],
                                          permissionName: '访问评价聚合页',
                                          permissionGroupName: '消息',
                                          platformStoreScopes:
                                            getFieldValue(fields.platformStoreScopes) || [],
                                        },
                                      ],
                                      'permissionId',
                                    ),
                                  },
                                ]
                              : []),
                          ]);
                        }}
                      >
                        {t('negative_review_reception')}
                      </Checkbox>
                    </div>
                  );
                }}
              </FormItem>
            )}
          </>
        );
      }}
    </FormItem>
  );
};
export default AssignConversationConfigFields;
